import { BaseRepository } from './BaseRepository';

/**
 * Objection Response entity type
 */
export interface ObjectionResponse {
  id: string;
  company_id: string;
  objection_text: string;
  response_template: string;
  category: 'price' | 'hesitation' | 'competition' | 'coverage' | 'timing' | 'trust' | 'other';
  effectiveness_score: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Repository for managing objection responses
 * Provides CRUD operations for objection handling library
 */
export class ObjectionResponseRepository extends BaseRepository<ObjectionResponse> {
  constructor() {
    super({ tableName: 'objection_responses', primaryKey: 'id' });
  }

  /**
   * Find all objection responses for a specific company
   */
  async findByCompany(companyId: string): Promise<ObjectionResponse[]> {
    return this.findAll({
      filters: { company_id: companyId },
      orderBy: { column: 'effectiveness_score', ascending: false }
    });
  }

  /**
   * Find objection responses by category
   */
  async findByCategory(
    companyId: string, 
    category: ObjectionResponse['category']
  ): Promise<ObjectionResponse[]> {
    return this.findAll({
      filters: { company_id: companyId, category },
      orderBy: { column: 'effectiveness_score', ascending: false }
    });
  }

  /**
   * Get top performing objection responses
   */
  async getTopPerforming(
    companyId: string, 
    limit: number = 10
  ): Promise<ObjectionResponse[]> {
    return this.findAll({
      filters: { company_id: companyId },
      orderBy: { column: 'effectiveness_score', ascending: false },
      limit
    });
  }

  /**
   * Increment usage count for an objection response
   */
  async incrementUsage(id: string): Promise<ObjectionResponse> {
    const response = await this.findById(id);
    if (!response) {
      throw new Error('Objection response not found');
    }

    return this.update(id, { 
      usage_count: response.usage_count + 1 
    });
  }

  /**
   * Update effectiveness score
   */
  async updateEffectiveness(
    id: string, 
    newScore: number
  ): Promise<ObjectionResponse> {
    if (newScore < 0 || newScore > 5) {
      throw new Error('Effectiveness score must be between 0 and 5');
    }

    return this.update(id, { effectiveness_score: newScore });
  }

  /**
   * Get most frequently used objections
   */
  async getMostUsed(
    companyId: string, 
    limit: number = 10
  ): Promise<ObjectionResponse[]> {
    return this.findAll({
      filters: { company_id: companyId },
      orderBy: { column: 'usage_count', ascending: false },
      limit
    });
  }
}

// Export singleton instance
export const objectionResponseRepository = new ObjectionResponseRepository();
